import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

/**
 * Ein Klappmenü für die Aktionen einer Tabellenzeile.
 *
 * WOFÜR: Acht untereinander stehende Verweise je Zeile machen eine Tabelle mit
 * vier Servern über tausend Pixel hoch — und jede neue Funktion machte es
 * schlimmer. Gebündelt bleibt die Zeile so hoch wie ihr Inhalt.
 *
 * ZWEI DINGE, DIE HIER LEICHT SCHIEFGEHEN:
 *
 *  1. **Abgeschnittene Menüs.** Ein absolut positioniertes Menü in einer
 *     Tabelle mit `overflow-x: auto` wird am Rand des Containers abgeschnitten
 *     — beim letzten Eintrag der letzten Zeile also genau dann, wenn man es
 *     braucht. Deshalb hängt es an `document.body` und rechnet seine Lage aus
 *     dem Rechteck des Knopfes aus.
 *  2. **Ein Menü, das offen bleibt.** Es schließt bei Klick daneben, bei Escape
 *     und beim Scrollen. Letzteres, weil die berechnete Lage sonst mitwandert,
 *     ohne dass das Menü es merkt.
 */

export interface Aktion {
  label: string;
  onClick: () => void;
  /** Wird als Titel angezeigt — bei gesperrten Einträgen der Grund. */
  titel?: string;
  gesperrt?: boolean;
  /** Rot und unten, hinter einer Trennlinie. */
  gefaehrlich?: boolean;
  symbol?: React.ReactNode;
}

export default function AktionsMenue({
  aktionen,
  beschriftung = 'Aktionen',
}: {
  aktionen: Aktion[];
  beschriftung?: string;
}) {
  const [offen, setOffen] = useState(false);
  const [lage, setLage] = useState<{ top: number; left: number } | null>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const menueRef = useRef<HTMLDivElement>(null);

  // Vor dem Zeichnen rechnen, damit das Menü nicht sichtbar springt.
  useLayoutEffect(() => {
    if (!offen || !knopfRef.current) return;
    const r = knopfRef.current.getBoundingClientRect();
    const breite = 190;
    setLage({
      top: r.bottom + 4,
      // Rechtsbündig am Knopf, aber nie über den Fensterrand hinaus.
      left: Math.max(8, Math.min(r.right - breite, window.innerWidth - breite - 8)),
    });
  }, [offen]);

  useEffect(() => {
    if (!offen) return;

    const beiKlick = (e: MouseEvent) => {
      const z = e.target as Node;
      if (menueRef.current?.contains(z) || knopfRef.current?.contains(z)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false);
    };
    const beiBewegung = () => setOffen(false);

    document.addEventListener('mousedown', beiKlick);
    document.addEventListener('keydown', beiTaste);
    // `true` für die Erfassungsphase: Scrollen passiert oft in einem inneren
    // Container, und ohne das bekäme das Fenster davon nichts mit.
    window.addEventListener('scroll', beiBewegung, true);
    window.addEventListener('resize', beiBewegung);
    return () => {
      document.removeEventListener('mousedown', beiKlick);
      document.removeEventListener('keydown', beiTaste);
      window.removeEventListener('scroll', beiBewegung, true);
      window.removeEventListener('resize', beiBewegung);
    };
  }, [offen]);

  const normal = aktionen.filter((a) => !a.gefaehrlich);
  const heikel = aktionen.filter((a) => a.gefaehrlich);

  function eintrag(a: Aktion, i: number) {
    return (
      <button
        key={`${a.label}-${i}`}
        role="menuitem"
        disabled={a.gesperrt}
        title={a.titel}
        onClick={() => {
          setOffen(false);
          a.onClick();
        }}
        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ${
          a.gefaehrlich
            ? 'text-red-600 hover:bg-red-50'
            : 'text-slate-700 hover:bg-slate-50'
        } disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent`}
      >
        {a.symbol}
        {a.label}
      </button>
    );
  }

  return (
    <>
      <button
        ref={knopfRef}
        onClick={() => setOffen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={offen}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        <MoreHorizontal size={13} />
        {beschriftung}
      </button>

      {offen &&
        lage &&
        createPortal(
          <div
            ref={menueRef}
            role="menu"
            style={{ position: 'fixed', top: lage.top, left: lage.left, width: 190 }}
            className="z-[70] overflow-hidden rounded-lg border border-slate-200 bg-surface py-1 shadow-lg"
          >
            {normal.map(eintrag)}
            {heikel.length > 0 && normal.length > 0 && (
              <div className="my-1 border-t border-slate-100" />
            )}
            {heikel.map(eintrag)}
          </div>,
          document.body,
        )}
    </>
  );
}
